import { buildRoute } from './libs';

export const ROUTES = {
    BASE: '/',
    LOGOUT: '/logout',

    AUTH: {
        GITHUB_CALLBACK: `/auth/github/callback`,
        OKTA_CALLBACK: `/auth/okta/callback`,
        ENTRA_CALLBACK: `/auth/entra/callback`,
        GOOGLE_CALLBACK: `/auth/google/callback`,
        TOKEN: `/auth/token`,
    },

    PROJECT: {
        LIST: '/projects',
        ADD: '/projects/add',
        DETAILS: {
            TEMPLATE: `/projects/:projectName`,
            FORMAT: (projectName: string) => buildRoute(ROUTES.PROJECT.DETAILS.TEMPLATE, { projectName }),
            SETTINGS: {
                TEMPLATE: `/projects/:projectName`,
                FORMAT: (projectName: string) => buildRoute(ROUTES.PROJECT.DETAILS.SETTINGS.TEMPLATE, { projectName }),
            },

            EVENTS: {
                TEMPLATE: `/projects/:projectName/events`,
                FORMAT: (projectName: string) => buildRoute(ROUTES.PROJECT.DETAILS.EVENTS.TEMPLATE, { projectName }),
            },

            RUNS: {
                DETAILS: {
                    TEMPLATE: `/projects/:projectName/runs/:runId`,
                    FORMAT: (projectName: string, runId: string) =>
                        buildRoute(ROUTES.PROJECT.DETAILS.RUNS.DETAILS.TEMPLATE, { projectName, runId }),
                    METRICS: {
                        TEMPLATE: `/projects/:projectName/runs/:runId/metrics`,
                        FORMAT: (projectName: string, runId: string) =>
                            buildRoute(ROUTES.PROJECT.DETAILS.RUNS.DETAILS.METRICS.TEMPLATE, { projectName, runId }),
                    },
                    EVENTS: {
                        TEMPLATE: `/projects/:projectName/runs/:runId/events`,
                        FORMAT: (projectName: string, runId: string) =>
                            buildRoute(ROUTES.PROJECT.DETAILS.RUNS.DETAILS.EVENTS.TEMPLATE, { projectName, runId }),
                    },
                    LOGS: {
                        TEMPLATE: `/projects/:projectName/runs/:runId/logs`,
                        FORMAT: (projectName: string, runId: string) =>
                            buildRoute(ROUTES.PROJECT.DETAILS.RUNS.DETAILS.LOGS.TEMPLATE, { projectName, runId }),
                    },
                    INSPECT: {
                        TEMPLATE: `/projects/:projectName/runs/:runId/inspect`,
                        FORMAT: (projectName: string, runId: string) =>
                            buildRoute(ROUTES.PROJECT.DETAILS.RUNS.DETAILS.INSPECT.TEMPLATE, { projectName, runId }),
                    },
                    JOBS: {
                        DETAILS: {
                            TEMPLATE: `/projects/:projectName/runs/:runId/jobs/:jobName`,
                            FORMAT: (projectName: string, runId: string, jobName: string) =>
                                buildRoute(ROUTES.PROJECT.DETAILS.RUNS.DETAILS.JOBS.DETAILS.TEMPLATE, {
                                    projectName,
                                    runId,
                                    jobName,
                                }),
                            METRICS: {
                                TEMPLATE: `/projects/:projectName/runs/:runId/jobs/:jobName/metrics`,
                                FORMAT: (projectName: string, runId: string, jobName: string) =>
                                    buildRoute(ROUTES.PROJECT.DETAILS.RUNS.DETAILS.JOBS.DETAILS.METRICS.TEMPLATE, {
                                        projectName,
                                        runId,
                                        jobName,
                                    }),
                            },
                            LOGS: {
                                TEMPLATE: `/projects/:projectName/runs/:runId/jobs/:jobName/logs`,
                                FORMAT: (projectName: string, runId: string, jobName: string) =>
                                    buildRoute(ROUTES.PROJECT.DETAILS.RUNS.DETAILS.JOBS.DETAILS.LOGS.TEMPLATE, {
                                        projectName,
                                        runId,
                                        jobName,
                                    }),
                            },
                            EVENTS: {
                                TEMPLATE: `/projects/:projectName/runs/:runId/jobs/:jobName/events`,
                                FORMAT: (projectName: string, runId: string, jobName: string) =>
                                    buildRoute(ROUTES.PROJECT.DETAILS.RUNS.DETAILS.JOBS.DETAILS.EVENTS.TEMPLATE, {
                                        projectName,
                                        runId,
                                        jobName,
                                    }),
                            },
                        },
                    },
                },
            },
        },
        BACKEND: {
            ADD: {
                TEMPLATE: `/projects/:projectName/backends/add`,
                FORMAT: (projectName: string) => buildRoute(ROUTES.PROJECT.BACKEND.ADD.TEMPLATE, { projectName }),
            },
            EDIT: {
                TEMPLATE: `/projects/:projectName/backends/:backend`,
                FORMAT: (projectName: string, backendName: string) =>
                    buildRoute(ROUTES.PROJECT.BACKEND.EDIT.TEMPLATE, { projectName, backend: backendName }),
            },
        },
        GATEWAY: {
            ADD: {
                TEMPLATE: `/projects/:projectName/gateways/add`,
                FORMAT: (projectName: string) => buildRoute(ROUTES.PROJECT.GATEWAY.ADD.TEMPLATE, { projectName }),
            },
            EDIT: {
                TEMPLATE: `/projects/:projectName/gateways/:instance`,
                FORMAT: (projectName: string, instanceName: string) =>
                    buildRoute(ROUTES.PROJECT.GATEWAY.EDIT.TEMPLATE, { projectName, instance: instanceName }),
            },
        },
    },

    EVENTS: {
        LIST: '/events',
    },

    RUNS: {
        LIST: '/runs',
        CREATE_DEV_ENV: '/runs/launch',
    },

    OFFERS: {
        LIST: '/offers',
    },

    MODELS: {
        LIST: '/models',
        DETAILS: {
            TEMPLATE: `/projects/:projectName/models/:runName`,
            FORMAT: (projectName: string, runName: string) =>
                buildRoute(ROUTES.MODELS.DETAILS.TEMPLATE, { projectName, runName }),
        },
    },

    FLEETS: {
        LIST: '/fleets',
        ADD: {
            TEMPLATE: `/projects/:projectName/fleets/add`,
            FORMAT: (projectName: string) => buildRoute(ROUTES.FLEETS.ADD.TEMPLATE, { projectName }),
        },
        DETAILS: {
            TEMPLATE: `/projects/:projectName/fleets/:fleetId`,
            FORMAT: (projectName: string, fleetId: string) =>
                buildRoute(ROUTES.FLEETS.DETAILS.TEMPLATE, { projectName, fleetId }),
            EVENTS: {
                TEMPLATE: `/projects/:projectName/fleets/:fleetId/events`,
                FORMAT: (projectName: string, fleetId: string) =>
                    buildRoute(ROUTES.FLEETS.DETAILS.EVENTS.TEMPLATE, { projectName, fleetId }),
            },
            INSPECT: {
                TEMPLATE: `/projects/:projectName/fleets/:fleetId/inspect`,
                FORMAT: (projectName: string, fleetId: string) =>
                    buildRoute(ROUTES.FLEETS.DETAILS.INSPECT.TEMPLATE, { projectName, fleetId }),
            },
        },
    },

    INSTANCES: {
        LIST: '/instances',
        DETAILS: {
            TEMPLATE: `/projects/:projectName/instances/:instanceId`,
            FORMAT: (projectName: string, instanceId: string) =>
                buildRoute(ROUTES.INSTANCES.DETAILS.TEMPLATE, { projectName, instanceId }),
            EVENTS: {
                TEMPLATE: `/projects/:projectName/instances/:instanceId/events`,
                FORMAT: (projectName: string, instanceId: string) =>
                    buildRoute(ROUTES.INSTANCES.DETAILS.EVENTS.TEMPLATE, { projectName, instanceId }),
            },
            INSPECT: {
                TEMPLATE: `/projects/:projectName/instances/:instanceId/inspect`,
                FORMAT: (projectName: string, instanceId: string) =>
                    buildRoute(ROUTES.INSTANCES.DETAILS.INSPECT.TEMPLATE, { projectName, instanceId }),
            },
        },
    },

    VOLUMES: {
        LIST: '/volumes',
    },

    PRESETS: {
        LIST: '/presets',
        DETAILS: {
            TEMPLATE: `/presets/:projectName/:presetId`,
            FORMAT: (projectName: string, presetId: string) =>
                buildRoute(ROUTES.PRESETS.DETAILS.TEMPLATE, { projectName, presetId }),
            VERIFIED_ON: {
                TEMPLATE: `/presets/:projectName/:presetId/verified-on`,
                FORMAT: (projectName: string, presetId: string) =>
                    buildRoute(ROUTES.PRESETS.DETAILS.VERIFIED_ON.TEMPLATE, { projectName, presetId }),
            },
            INSPECT: {
                TEMPLATE: `/presets/:projectName/:presetId/inspect`,
                FORMAT: (projectName: string, presetId: string) =>
                    buildRoute(ROUTES.PRESETS.DETAILS.INSPECT.TEMPLATE, { projectName, presetId }),
            },
        },
    },

    USER: {
        LIST: '/users',
        ADD: '/users/add',
        DETAILS: {
            TEMPLATE: `/users/:userName`,
            FORMAT: (userName: string) => buildRoute(ROUTES.USER.DETAILS.TEMPLATE, { userName }),
        },
        EDIT: {
            TEMPLATE: `/users/:userName/edit`,
            FORMAT: (userName: string) => buildRoute(ROUTES.USER.EDIT.TEMPLATE, { userName }),
        },
        PROJECTS: {
            TEMPLATE: `/users/:userName/projects`,
            FORMAT: (userName: string) => buildRoute(ROUTES.USER.PROJECTS.TEMPLATE, { userName }),
        },
        EVENTS: {
            TEMPLATE: `/users/:userName/events`,
            FORMAT: (userName: string) => buildRoute(ROUTES.USER.EVENTS.TEMPLATE, { userName }),
        },
        PUBLIC_KEYS: {
            TEMPLATE: `/users/:userName/public-keys`,
            FORMAT: (userName: string) => buildRoute(ROUTES.USER.PUBLIC_KEYS.TEMPLATE, { userName }),
        },

        BILLING: {
            LIST: {
                TEMPLATE: `/users/:userName/billing`,
                FORMAT: (userName: string) => buildRoute(ROUTES.USER.BILLING.LIST.TEMPLATE, { userName }),
            },
            ADD_PAYMENT: {
                TEMPLATE: `/users/:userName/billing/payments/add`,
                FORMAT: (userName: string) => buildRoute(ROUTES.USER.BILLING.ADD_PAYMENT.TEMPLATE, { userName }),
            },
        },
    },

    BILLING: {
        BALANCE: '/billing',
    },
};
